import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { KibButtonNew } from '@chewy/kib-controls-react'
import moment from 'moment'
import { DateUtils } from '@pbt/pbt-ui-components'

import type {
  ChatHistoryOption,
  ChatHistorySectionKey,
} from './ChatHistoryDropdown'

import styles from './ChatHistoryDropdown.module.scss'

function getSectionKey(ts: number): ChatHistorySectionKey {
  const m = moment.unix(ts)
  const todayStart = moment().startOf('day')
  const yesterdayStart = moment().subtract(1, 'day').startOf('day')
  const thisWeekStart = moment().subtract(7, 'day').startOf('day')
  if (m.isSameOrAfter(todayStart)) return 'today'
  if (m.isSameOrAfter(yesterdayStart)) return 'yesterday'
  if (m.isSameOrAfter(thisWeekStart)) return 'thisWeek'
  return 'previous'
}

function formatTimestamp(ts: number, section: ChatHistorySectionKey): string {
  const iso = moment.unix(ts).toISOString()
  const format = section === 'today' ? 'h:mma' : 'MM/DD/YY h:mma'
  return DateUtils.formatDate(iso, false, format)
}

const SECTION_ORDER: ChatHistorySectionKey[] = [
  'today',
  'yesterday',
  'thisWeek',
  'previous',
]

export interface ChatHistoryDropdownMenuProps {
  onClose: () => void
  onSelect: (value: string) => void
  options: ChatHistoryOption[]
  value: string
}

const ChatHistoryDropdownMenu: React.FC<ChatHistoryDropdownMenuProps> = ({
  options,
  value,
  onSelect,
  onClose,
}) => {
  const { t } = useTranslation(['Agent', 'Time'])
  const sections = useMemo(() => {
    const bySection: Record<ChatHistorySectionKey, ChatHistoryOption[]> = {
      today: [],
      yesterday: [],
      thisWeek: [],
      previous: [],
    }
    options
      .filter((opt) => opt.value)
      .forEach((opt) => {
        const key =
          opt.lastUpdateTime != null
            ? getSectionKey(opt.lastUpdateTime)
            : 'previous'
        bySection[key].push(opt)
      })
    return SECTION_ORDER.map((key) => ({
      key,
      options: bySection[key],
    })).filter((s) => s.options.length > 0)
  }, [options])

  const emptyPlaceholder =
    options.length === 1 && !options[0].value ? options[0] : null

  const sectionLabels: Record<ChatHistorySectionKey, string> = {
    today: t('Time:LABEL.TODAY'),
    yesterday: t('Time:LABEL.YESTERDAY'),
    thisWeek: t('Time:LABEL.THIS_WEEK'),
    previous: t('Time:LABEL.PAST'),
  }

  const handleOptionClick = (optionValue: string) => {
    if (optionValue) {
      onSelect(optionValue)
    }
    onClose()
  }

  const selectableCount = options.filter((o) => o.value).length

  return (
    <div className={styles.menuWrapper}>
      {selectableCount > 1 && (
        <div className={styles.optionHeader} role="presentation">
          {t('Agent:AVAILABLE_OPTIONS', { optionCount: selectableCount })}
        </div>
      )}
      <ul
        aria-label={t('Agent:PAST_CHATS')}
        className={styles.menu}
        role="listbox"
      >
        {emptyPlaceholder && (
          <li>
            <KibButtonNew
              disabled
              aria-selected={false}
              className={styles.menuButton}
              emphasis="tertiary"
              role="option"
              theme="utility"
            >
              <span className={styles.menuButtonLabel}>
                {emptyPlaceholder.label}
              </span>
            </KibButtonNew>
          </li>
        )}
        {sections.map(({ key, options: sectionOptions }) => (
          <li
            aria-label={sectionLabels[key]}
            className={styles.section}
            key={key}
            role="group"
          >
            <div className={styles.sectionLabel}>{sectionLabels[key]}</div>
            {sectionOptions.map((option) => (
              <KibButtonNew
                aria-selected={option.value === value}
                className={styles.menuButton}
                disabled={!option.value}
                emphasis="tertiary"
                key={option.value}
                role="option"
                theme="utility"
                onClick={() => handleOptionClick(option.value)}
              >
                <span className={styles.menuButtonContent}>
                  <span className={styles.menuButtonLabel}>
                    <span className={styles.menuButtonTitle}>
                      {option.label}
                    </span>
                    {option.value === value && (
                      <span>{t('Agent:CURRENTLY_VIEWING')}</span>
                    )}
                  </span>
                  {option.lastUpdateTime != null && (
                    <span className={styles.timestamp}>
                      {formatTimestamp(option.lastUpdateTime, key)}
                    </span>
                  )}
                </span>
              </KibButtonNew>
            ))}
          </li>
        ))}
      </ul>
    </div>
  )
}

export default ChatHistoryDropdownMenu
